import React, { useState } from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { Checkbox } from '../ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MiniButton } from '../ui/inputs/MiniButton/MiniButton';
import { Notifiable, NotifyDTO } from '../../store/table/types';
import { AppState } from '../../store/state';
import { selectAssignmentState } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import SimpleDialog from '../dialogs/SimpleDialog/SimpleDialog';
import { ReviewerConflictType } from '../../store/conference/types';
import WarningIcon from '../WarningIcon/WarningIcon';

interface Props extends GridCellProps {
  updatingId: number | null;
  idKey: string;
  handleAssignedClick?: (id: number, assigned: boolean, assignmentId: number, skipConflicts?: boolean) => void;
  notifiableIds: {
    [id: number]: Notifiable;
  };
  handleNotifyClick: (data: NotifyDTO) => void;
}

const AssignedCell: React.FC<Props> = ({
  updatingId,
  idKey,
  handleAssignedClick,
  notifiableIds,
  handleNotifyClick,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);

  const field = props.field || '';
  const isAssigned = props.dataItem[field];
  const modelId = props.dataItem[idKey];
  const assignmentId: number = props.dataItem.assignment_id ?? 0;
  const notifiable = notifiableIds[assignmentId];
  const conflicts: string = props.dataItem['conflicts'] ?? '';

  const description: { [key in ReviewerConflictType]: string } = {
    affiliation: t("There is a coincidence between one of the author's affiliations and the reviewer's affiliation"),
    bid: t('Reviewer has manually set that has a conflict with this submission'),
    'co-author': t("One of the authors is in the list of reviewer's co-author"),
    'email-domain': t(
      "There is a coincidence between one of the author's email domain and the reviewer's email domain",
    ),
  };

  return (
    <StyledTd
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
      aria-colindex={props.ariaColumnIndex}
      aria-selected={props.isSelected}
      style={props.style}
    >
      {updatingId == modelId ? (
        <p>{t('Working...')}</p>
      ) : (
        <div className="flex items-center">
          <Checkbox
            checked={isAssigned}
            onChange={(event) => {
              if (handleAssignedClick) {
                const { checked } = event.target;
                if (conflicts && checked) {
                  const title = (
                    <div className="flex items-center gap-x-1">
                      <WarningIcon />
                      <div>
                        <span>{t('Conflict of interest detected')}</span>
                      </div>
                    </div>
                  );
                  setDialog(
                    <SimpleDialog
                      title={title}
                      handleClose={() => setDialog(undefined)}
                      handleOk={() => handleAssignedClick(modelId, checked, assignmentId, true)}
                      width="31.25rem"
                    >
                      <div className="leading-normal">
                        <p className="mb-1">
                          {t(
                            'This assignment cannot be completed because a conflict of interest has been detected between the reviewer and the submission:',
                          )}
                        </p>
                        <dl className="pl-4">
                          {conflicts.split(',').map((value, index) => {
                            const type = value as ReviewerConflictType;
                            return (
                              <li key={index}>
                                <span>{description[type]}</span>
                              </li>
                            );
                          })}
                        </dl>
                        <div className="mt-4">
                          <p>
                            If you proceed with this assignment, the conflict <b>will be explicitly ignored</b> and the
                            reviewer will be assigned despite the conflict.
                          </p>
                        </div>
                      </div>
                    </SimpleDialog>,
                  );
                } else {
                  handleAssignedClick(modelId, checked, assignmentId);
                }
              }
            }}
          />
          {notifiable && (
            <MiniButton
              labelText={notifiable.isDone ? t('Notified') : t('Notify')}
              success={notifiable.isDone}
              onButtonClick={notifiable.isDone ? undefined : () => handleNotifyClick({ assignment_id: assignmentId })}
            />
          )}
        </div>
      )}
      {dialog}
    </StyledTd>
  );
};

const mapStateToProps = (state: AppState) => ({
  updatingId: selectAssignmentState(state).updatingId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AssignedCell);

const StyledTd = styled.td`
  .notify {
    color: ${(props) => props.theme.palette.primary.main};
    border-color: ${(props) => props.theme.palette.primary.main};
    cursor: pointer;
  }

  .notified {
    color: #1f9801;
    background-color: #e4f0e1;
  }
`;
